/**
 * Variant Picker Dropdown
 * -----------------------
 * Extracted from snippets/product-variant-picker-block.liquid where this
 * lived as a ~650-line inline <script> rendered per quick-view / PDP.
 * On collection pages with many cards (e.g. /collections/all on iOS
 * Safari) every quick-view AJAX inject re-parsed the full script body,
 * pushing the WebKit JS heap over the OOM threshold and triggering the
 * "Problemen met deze pagina" tab kill.
 *
 * Loaded once via layout/theme.liquid (browser caches the file). The
 * inline stub left in the snippet just calls `cnInitVariantPicker(picker)`
 * for the picker being rendered. AJAX-injected quick-views therefore add
 * a couple of inline-stub bytes instead of 650 lines of parser work.
 *
 * The function body is verbatim from the original inline IIFE — only the
 * picker-discovery shim at the top was moved out (it now lives in the
 * inline stub in the snippet so we get the right picker for the current
 * insertion) and the IIFE wrapper was replaced by a reusable function
 * that takes `picker` as a parameter so it can be invoked per insertion.
 */
(function () {
  'use strict';

  function initVariantPicker(picker) {
    if (!picker || picker.hasAttribute('data-dropdown-init')) return;
    picker.setAttribute('data-dropdown-init', '');

          const variantsScript = picker.querySelector('script[data-variant-picker-variants]');
          if (!variantsScript) return;

          // CSS.escape fallback for older browsers
          const _esc = (s) => typeof CSS !== 'undefined' && CSS.escape ? CSS.escape(s) : s.replace(/([:.\[\]])/g, '\\$1');

          // Sold-out badge text: "✕" for size options, "SOLD OUT" for others
          const _sizeKeywords = ['maat', 'size', 'größe', 'grosse'];
          const _isSizeOption = (label) => {
            // Check closest group with option-picker-name, or fallback to data-variant-picker-option-labels
            const group = label.closest('[option-picker-name]') || label.closest('[data-variant-picker-option-labels]');
            const groupName = (group?.getAttribute('option-picker-name') || '').toLowerCase();
            if (_sizeKeywords.some(k => groupName.includes(k))) return true;
            // Fallback: check the option position and match against product options
            const pos = parseInt(label.getAttribute('data-option-position'), 10);
            if (pos) {
              const optionNames = Array.from(picker.querySelectorAll('[option-picker-name]')).map(g => ({
                pos: parseInt((g.querySelector('[data-option-position]')?.getAttribute('data-option-position')) || '0', 10),
                name: (g.getAttribute('option-picker-name') || '').toLowerCase()
              }));
              const match = optionNames.find(o => o.pos === pos);
              if (match) return _sizeKeywords.some(k => match.name.includes(k));
            }
            return false;
          };
          const _soldOutText = (label) => _isSizeOption(label) ? '✕' : 'SOLD OUT';
          const _soldOutClass = (label) => 'sold-out-badge' + (_isSizeOption(label) ? ' sold-out-badge--x' : '');

          const _firstOptionPos = null;

          const includeOutOfStockAsExists = true;
          const hideFalsePills = true;
          const hideFalseDropdownItems = true;
          const doNotHideOptions = [];

          let variants = [];
          try {
            variants = JSON.parse(variantsScript.textContent || '[]');
          } catch (e) {}

          // Detect smaak option position
          const smaakGroup = picker.querySelector('.smaak-variant');
          const smaakPosAttr = smaakGroup?.querySelector('[data-option-position]')?.getAttribute('data-option-position');
          const smaakOptionPosition = smaakPosAttr ? parseInt(smaakPosAttr, 10) : null;

          function getSelected() {
            const sel = {};
            for (let pos = 1; pos <= 3; pos++) {
              const inp = picker.querySelector('input[type="radio"][name$="-' + pos + '"]:checked');
              if (inp) sel[pos] = inp.value;
            }
            return sel;
          }

          function existsFor(pos, val, selected) {
            return variants.some((v) => {
              if (!includeOutOfStockAsExists && !v.available) return false;
              if (v.hideinshop === true) return false;

              const v1 = String(v.option1 || '');
              const v2 = String(v.option2 || '');
              const v3 = String(v.option3 || '');
              if (pos === 1 && v1 !== String(val)) return false;
              if (pos === 2 && v2 !== String(val)) return false;
              if (pos === 3 && v3 !== String(val)) return false;
              if (pos !== 1 && selected[1] && v1 !== String(selected[1])) return false;
              if (pos !== 2 && selected[2] && v2 !== String(selected[2])) return false;
              if (pos !== 3 && selected[3] && v3 !== String(selected[3])) return false;
              return true;
            });
          }

          function isAvailableFor(pos, val, selected) {
            return variants.some((v) => {
              if (!v.available) return false;
              if (v.hideinshop === true) return false;
              const v1 = String(v.option1 || '');
              const v2 = String(v.option2 || '');
              const v3 = String(v.option3 || '');
              if (pos === 1 && v1 !== String(val)) return false;
              if (pos === 2 && v2 !== String(val)) return false;
              if (pos === 3 && v3 !== String(val)) return false;
              if (pos !== 1 && selected[1] && v1 !== String(selected[1])) return false;
              if (pos !== 2 && selected[2] && v2 !== String(selected[2])) return false;
              if (pos !== 3 && selected[3] && v3 !== String(selected[3])) return false;
              return true;
            });
          }

          function updateGroup(selectorLabels, hideFalse) {
            const selected = getSelected();
            const labels = picker.querySelectorAll(selectorLabels);
            labels.forEach((label) => {
              const pos = parseInt(label.getAttribute('data-option-position'), 10);
              const val = label.getAttribute('data-option-value');
              const optionName = label
                .closest('[data-variant-picker-option-labels]')
                ?.getAttribute('option-picker-name')
                ?.toLowerCase();
              const isSmaak = label.closest('.smaak-variant') !== null || (smaakOptionPosition !== null && pos === smaakOptionPosition);
              const isPrimaryNonSmaak = !smaakOptionPosition && _firstOptionPos && pos === _firstOptionPos;
              const isSecondary = label.closest('.variant-option-group--secondary') !== null;
              const ok = existsFor(pos, val, selected);

              const inputId = label.getAttribute('for');
              const input = inputId ? picker.querySelector('#' + inputId) : null;

              // Primary option pills (smaak/kleur): always show, grey out only if ALL variants sold out
              if (isSmaak || isPrimaryNonSmaak) {
                const smaakVal = val;
                const isHidden = variants.every(v => {
                  const vSmaak = String(v[`option${pos}`] || '');
                  return vSmaak !== String(smaakVal) || v.hideinshop === true;
                });
                label.style.display = isHidden ? 'none' : '';
                if (!isHidden) {
                  // Grey out primary pills when ALL variants for that smaak
                  // are sold out — applies to bundle AND standalone PDPs.
                  // The pill stays clickable so Klaviyo BIS can still trigger.
                  const hasAvail = variants.some(v => {
                    if (v.hideinshop === true || !v.available) return false;
                    return String(v[`option${pos}`] || '') === String(smaakVal);
                  });
                  label.style.display = '';
                  if (!hasAvail) {
                    label.classList.add('variant-unavailable');
                    if (input && input.checked) label.classList.add('selected');
                    else label.classList.remove('selected');
                    label.style.setProperty('opacity', '0.5', 'important');
                  } else {
                    label.classList.remove('variant-unavailable', 'variant-disabled');
                    label.style.setProperty('opacity', '1', 'important');
                  }
                  label.style.setProperty('pointer-events', 'auto', 'important');
                  label.removeAttribute('no-available');
                  if (input) input.disabled = false;
                  const oldSob = label.querySelector('.sold-out-badge');
                  if (oldSob) oldSob.remove();
                } else {
                  if (input) input.disabled = true;
                }
              } else if (doNotHideOptions.includes(optionName)) {
                if (!ok) {
                  label.classList.add('variant-disabled');
                  if (input) input.disabled = true;
                } else {
                  label.classList.remove('variant-disabled');
                  if (input) input.disabled = false;
                }
              } else {
                if (hideFalse) {
                  // Check against selected smaak (or first option for non-smaak products)
                  const primaryPos = smaakOptionPosition || _firstOptionPos;
                  const smaakSel = primaryPos ? selected[primaryPos] : null;
                  const existsForSmaak = smaakSel ? variants.some(v => {
                    if (v.hideinshop === true) return false;
                    return String(v[`option${primaryPos}`] || '') === String(smaakSel) &&
                           String(v[`option${pos}`] || '') === String(val);
                  }) : ok;
                  if (existsForSmaak) {
                    label.style.display = '';
                    // Remove old sold-out badge
                    const oldSobSec = label.querySelector('.sold-out-badge');
                    if (oldSobSec) oldSobSec.remove();
                    // Check if actually available (in stock)
                    const isInStock = smaakSel ? variants.some(v => {
                      if (v.hideinshop === true || !v.available) return false;
                      return String(v[`option${primaryPos}`] || '') === String(smaakSel) &&
                             String(v[`option${pos}`] || '') === String(val);
                    }) : variants.some(v => {
                      if (v.hideinshop === true || !v.available) return false;
                      if (String(v[`option${pos}`] || '') !== String(val)) return false;
                      // Check against all other selected options
                      for (let p = 1; p <= 3; p++) {
                        if (p === pos) continue;
                        if (selected[p] && String(v[`option${p}`] || '') !== String(selected[p])) return false;
                      }
                      return true;
                    });
                    if (isInStock) {
                      label.classList.remove('variant-unavailable');
                      // Clear stale inline opacity from a previous sold-out
                      // state so the pill returns to full opacity.
                      label.style.removeProperty('opacity');
                      if (input) input.disabled = false;
                    } else {
                      label.classList.add('variant-unavailable');
                      if (input) input.disabled = false;
                      // Keep 'selected' in sync with the actual checked radio so
                      // the gold border follows sold-out clicks too.
                      if (input && input.checked) label.classList.add('selected');
                      else label.classList.remove('selected');
                    }
                  } else {
                    label.style.display = 'none';
                    if (input) input.disabled = true;
                  }
                }
              }

            });
          }

          function updateDropdownTriggers() {
            picker.querySelectorAll('.variant-option-group.smaak-variant').forEach(group => {
              const pillList = group.querySelector('.product-variant-picker__pill-list');
              const dropdown = group.querySelector('[data-smaak-dropdown]');
              if (!pillList || !dropdown) return;
              const allPills = pillList.querySelectorAll('label[data-option-position]');
              let availableCount = 0;
              allPills.forEach(pill => {
                if (pill.style.display === 'none') return;
                if (pill.classList.contains('variant-disabled')) return;
                availableCount++;
              });
              if (availableCount <= 1) {
                dropdown.setAttribute('data-disabled', '');
                group.removeAttribute('data-dropdown-open');
              } else {
                dropdown.removeAttribute('data-disabled');
              }
            });
          }

          // Find input for a label — handles both nested inputs and for-attribute references
          function findLabelInput(lbl) {
            const inp = lbl.querySelector('input');
            if (inp) return inp;
            const forId = lbl.getAttribute('for');
            if (forId) return picker.querySelector('#' + _esc(forId));
            return null;
          }

          let _autoSelecting = false;
          function autoSelectFirstAvailable(forceBest) {
            if (_autoSelecting) return;
            _autoSelecting = true;
            picker.querySelectorAll('.variant-option-group--secondary').forEach(group => {
              if (['smaak','flavour','flavor','geschmack','kleur','color','farbe'].some(k => (group.getAttribute('option-picker-name') || '').toLowerCase().includes(k))) return;
              // Find currently checked input (check both nested and for-referenced)
              let checkedInput = null;
              let checkedLabel = null;
              group.querySelectorAll('.product-variant-picker__pill-list label[data-option-position]').forEach(lbl => {
                const inp = findLabelInput(lbl);
                if (inp && inp.checked) { checkedInput = inp; checkedLabel = lbl; }
              });
              // Preserve the user's current secondary across primary changes,
              // but only when it's actually purchasable for the new primary. A
              // sold-out pill is visible (Klaviyo back-in-stock needs it clickable)
              // so we must check variant-unavailable, not just display.
              // On initial load (forceBest=false) a sold-out default is still
              // respected so a back-in-stock URL isn't overridden.
              const isVisible = checkedLabel && checkedLabel.style.display !== 'none';
              const isAvailable = isVisible && !checkedLabel.classList.contains('variant-unavailable');
              if (isAvailable) return;
              if (isVisible && !forceBest) return;
              // Select most expensive available variant
              let bestInput = null;
              let bestLabel = null;
              let bestVal = -1;
              group.querySelectorAll('.product-variant-picker__pill-list label[data-option-position]').forEach(lbl => {
                if (lbl.style.display === 'none' || lbl.classList.contains('variant-unavailable')) return;
                const inp = findLabelInput(lbl);
                if (!inp) return;
                const text = lbl.querySelector('.pill__label')?.textContent || '';
                const num = parseFloat(text.replace(/[^\d.,]/g, '').replace(',', '.'));
                if (!isNaN(num) && num > bestVal) { bestVal = num; bestInput = inp; bestLabel = lbl; }
                else if (isNaN(num) && !bestInput) { bestInput = inp; bestLabel = lbl; }
              });
              // Fallback: if no available variant found, select first visible (even sold-out)
              if (!bestInput) {
                group.querySelectorAll('.product-variant-picker__pill-list label[data-option-position]').forEach(lbl => {
                  if (bestInput) return;
                  if (lbl.style.display === 'none') return;
                  const inp = findLabelInput(lbl);
                  if (inp) { bestInput = inp; bestLabel = lbl; }
                });
              }
              if (bestInput) {
                if (checkedLabel) checkedLabel.classList.remove('selected');
                bestInput.checked = true;
                if (bestLabel) bestLabel.classList.add('selected');
                bestInput.dispatchEvent(new Event('change', { bubbles: true }));
              }
            });
            _autoSelecting = false;
          }

          function updateAll(forceBest) {
            updateGroup('.product-variant-picker__pill-list label[data-option-position]', hideFalsePills);
            updateGroup('.variant-picker-dropdown__body label[data-option-position]', hideFalseDropdownItems);
            updateDropdownTriggers();
            autoSelectFirstAvailable(forceBest);
          }

          // Dynamic "Best Deal" badge on largest variant
          function applyBestDealBadge() {
            const bestDealText = (picker.getAttribute('data-best-deal-label') || '').trim();
            picker.querySelectorAll('.variant-option-group--secondary').forEach(group => {
              // Skip smaak-like and size options
              const groupName = (group.getAttribute('option-picker-name') || '').toLowerCase();
              if (['smaak', 'flavour', 'flavor', 'geschmack', 'kleur', 'color', 'farbe'].some(k => groupName.includes(k))) return;
              if (['maat', 'size', 'größe', 'grosse'].some(k => groupName.includes(k))) return;
              const allPills = group.querySelectorAll('label[data-option-position]');
              // Always strip existing badges first
              allPills.forEach(pill => {
                pill.classList.remove('best-deal');
                const existing = pill.querySelector('.best-deal-badge');
                if (existing) existing.remove();
              });
              // Empty setting disables the feature entirely
              if (!bestDealText) return;
              const pos = parseInt(allPills[0]?.getAttribute('data-option-position') || '0', 10);
              if (!pos) return;
              const parseNum = (s) => parseFloat(String(s || '').replace(/[^\d.,]/g, '').replace(',', '.'));
              const norm = (s) => String(s || '').trim().toLowerCase();
              const override = (picker.getAttribute('data-best-deal-override') || '').trim();
              const allVisible = [...allPills].filter(p => p.style.display !== 'none');
              let bestPill = null;
              if (override) {
                // Override: badge de variant uit het metafield (op getal of tekst).
                const ovNum = parseNum(override);
                bestPill = allVisible.find(pill => {
                  const text = pill.querySelector('.pill__label')?.textContent || '';
                  return norm(text) === norm(override) || (!isNaN(ovNum) && parseNum(text) === ovNum);
                });
              } else {
                // Automatisch: grootste inhoud over alle varianten.
                const distinct = new Set();
                let overallMax = -Infinity;
                variants.forEach(v => {
                  if (v.hideinshop === true) return;
                  const num = parseNum(v['option' + pos]);
                  if (isNaN(num)) return;
                  distinct.add(num);
                  if (num > overallMax) overallMax = num;
                });
                if (distinct.size <= 1 || overallMax === -Infinity) return;
                bestPill = allVisible.find(pill => {
                  const text = pill.querySelector('.pill__label')?.textContent || '';
                  return parseNum(text) === overallMax;
                });
              }
              if (!bestPill) return;
              bestPill.classList.add('best-deal');
              const badge = document.createElement('span');
              badge.className = 'best-deal-badge';
              badge.textContent = bestDealText;
              bestPill.appendChild(badge);
            });
          }

          function resetSmaakLabels() {
            const resetPos = smaakOptionPosition || _firstOptionPos;
            if (!resetPos) return;
            const smaakLabels = picker.querySelectorAll(
              `label[data-option-position="${resetPos}"]`
            );
            smaakLabels.forEach(label => {
              const val = label.getAttribute('data-option-value');
              const isHidden = variants.every(v => {
                return String(v[`option${resetPos}`] || '') !== String(val) || v.hideinshop === true;
              });
              if (!isHidden) {
                label.style.display = '';
                label.classList.remove('variant-disabled');
                label.removeAttribute('no-available');
                label.style.setProperty('pointer-events', 'auto', 'important');
                label.style.setProperty('cursor', 'pointer', 'important');
                const inputId = label.getAttribute('for');
                const input = inputId ? picker.querySelector('#' + inputId) : null;
                if (input) input.disabled = false;
                // Keep opacity for sold-out, full for available
                if (label.classList.contains('variant-unavailable')) {
                  label.style.setProperty('opacity', '0.5', 'important');
                } else {
                  label.style.setProperty('opacity', '1', 'important');
                }
              }
            });
          }

          updateAll();
          resetSmaakLabels();
          applyBestDealBadge();

          // Watch for theme setting no-available on ANY pill and remove it (all pills must stay clickable for Klaviyo)
          let _observerBusy = false;
          new MutationObserver((mutations) => {
            if (_observerBusy) return;
            const hasNoAvail = mutations.some(m => m.attributeName === 'no-available' && m.target.hasAttribute('no-available'));
            if (!hasNoAvail) return;
            _observerBusy = true;
            picker.querySelectorAll('label[no-available]').forEach(label => {
              if (label.style.display === 'none') return;
              label.removeAttribute('no-available');
              label.classList.remove('variant-disabled');
              label.style.setProperty('pointer-events', 'auto', 'important');
              label.style.setProperty('cursor', 'pointer', 'important');
              const isSoldOut = label.classList.contains('variant-unavailable');
              label.style.setProperty('opacity', isSoldOut ? '0.5' : '1', 'important');
              const inputId = label.getAttribute('for');
              const input = inputId ? picker.querySelector('#' + inputId) : null;
              if (input) input.disabled = false;
            });
            setTimeout(() => { _observerBusy = false; }, 50);
          }).observe(picker, { attributes: true, subtree: true, attributeFilter: ['no-available'] });

          // Sold-out pills are clickable (for Klaviyo back-in-stock) — no click blocking needed

          // Single change handler — detects smaak vs inhoud change
          picker.addEventListener('change', (e) => {
            if (_autoSelecting) return;
            if (e.target && e.target.matches('input[type="radio"]')) {
              // Detect smaak change: check if input is inside .smaak-variant OR matches smaak option position
              let isSmaakChange = !!e.target.closest('.smaak-variant');
              if (!isSmaakChange && smaakOptionPosition) {
                const inputName = e.target.name || '';
                isSmaakChange = inputName.endsWith('-' + smaakOptionPosition);
              }
              Promise.resolve().then(() => {
                updateAll(isSmaakChange);
                resetSmaakLabels();
                applyBestDealBadge();
              });
            }
          });

          // Sold-out pills are clickable (labels have pointer-events: auto, inputs not disabled)
          // The theme handles the variant change normally via label→input→change
          // We just need to enforce the selected state + ATC text after theme processes

          // Variant selector popup (mobile) & inline dropdown (desktop)
          const popupScrim = picker.querySelector('[data-variant-selector-scrim]');
          const popup = picker.querySelector('[data-variant-selector-popup]');
          const popupTitle = popup?.querySelector('[data-variant-selector-title]');
          const popupBody = popup?.querySelector('[data-variant-selector-body]');
          const popupClose = popup?.querySelector('[data-variant-selector-close]');
          const popupDrag = popup?.querySelector('[data-variant-selector-drag]');
          const isMobile = () => window.innerWidth <= 768;
          let activePopupGroup = null;

          const updateTrigger = (optionGroup, value, pillSwatch) => {
            const dropdown = optionGroup.querySelector('[data-smaak-dropdown]');
            if (!dropdown) return;
            const label = dropdown.querySelector('[data-smaak-dropdown-label]');
            const triggerSwatch = dropdown.querySelector('.smaak-mobile-dropdown__swatch');
            if (label) label.textContent = value;
            if (triggerSwatch && pillSwatch) {
              triggerSwatch.src = pillSwatch.src;
              triggerSwatch.alt = value;
              triggerSwatch.style.display = '';
            } else if (triggerSwatch && !pillSwatch) {
              triggerSwatch.style.display = 'none';
            }
          };

          const openPopup = (optionGroup) => {
            if (!popup || !popupBody || !popupTitle) return;
            activePopupGroup = optionGroup;
            popupTitle.textContent = optionGroup.getAttribute('option-picker-name') || '';

            while (popupBody.firstChild) popupBody.removeChild(popupBody.firstChild);
            const pillList = optionGroup.querySelector('.product-variant-picker__pill-list');
            if (pillList) {
              const clonedPills = pillList.cloneNode(true);
              clonedPills.classList.add('variant-selector-popup__pills');

              // Check if this is a smaak group
              const groupName = (optionGroup.getAttribute('option-picker-name') || '').toLowerCase();
              const isSmaakPopup = ['smaak', 'flavour', 'flavor', 'geschmack', 'kleur', 'color', 'farbe'].some(k => groupName.includes(k));

              // Mark sold-out pills in popup
              const selected = getSelected();
              clonedPills.querySelectorAll('label[data-option-position]').forEach(pill => {
                if (isSmaakPopup) {
                  // Primary smaak/kleur pills in the mobile popup — mirror the
                  // desktop PDP state. When every variant for a smaak is sold
                  // out, desktop greys the pill (variant-unavailable + 0.5
                  // opacity); the popup must show the same so a fully sold-out
                  // flavor is visibly unavailable. Pills stay clickable so
                  // Klaviyo back-in-stock still works.
                  const input = pill.querySelector('input');
                  pill.classList.remove('variant-disabled');
                  pill.style.display = '';
                  pill.style.setProperty('pointer-events', 'auto', 'important');
                  if (input) { input.disabled = false; input.removeAttribute('disabled'); }
                  if (pill.classList.contains('variant-unavailable')) {
                    pill.style.setProperty('opacity', '0.5', 'important');
                  } else {
                    pill.style.setProperty('opacity', '1', 'important');
                  }
                } else {
                  const pos = parseInt(pill.getAttribute('data-option-position'), 10);
                  const val = pill.getAttribute('data-option-value');
                  if (!isAvailableFor(pos, val, selected)) {
                    pill.classList.add('variant-sold-out');
                  }
                }
              });

              popupBody.appendChild(clonedPills);

              // Force enable smaak pills after DOM insertion — except sold-out ones
              if (isSmaakPopup) {
                const forceEnable = () => {
                  popupBody.querySelectorAll('label, .pill, input').forEach(el => {
                    // Skip elements inside a sold-out pill
                    if (el.classList.contains('variant-unavailable') || el.closest('.variant-unavailable')) return;
                    el.style.setProperty('opacity', '1', 'important');
                    el.style.setProperty('pointer-events', 'auto', 'important');
                    if (el.tagName === 'INPUT') {
                      el.disabled = false;
                      el.removeAttribute('disabled');
                    }
                    el.classList.remove('variant-disabled');
                  });
                };
                forceEnable();
                setTimeout(forceEnable, 100);
              }
            }

            popupScrim.style.display = '';
            popup.style.display = '';
            requestAnimationFrame(() => {
              popupScrim.classList.add('visible');
              popup.classList.add('open');
            });
            document.body.style.overflow = 'hidden';
          };

          const closePopup = () => {
            if (!popup) return;
            popup.classList.remove('open');
            popupScrim.classList.remove('visible');
            document.body.style.overflow = '';
            activePopupGroup = null;
            setTimeout(() => {
              popup.style.display = 'none';
              popupScrim.style.display = 'none';
            }, 220);
          };

          // Popup pill click — delegated
          if (popupBody) {
            popupBody.addEventListener('click', (e) => {
              const pill = e.target.closest('.pill');
              if (!pill || !activePopupGroup) return;
              // Block only invalid combos (variant-sold-out = no variant exists
              // for current primary). variant-unavailable means sold-out but a
              // valid combo — keep it clickable so Klaviyo BIS can trigger for
              // both primary and secondary. Without this, the label's `for=`
              // fallback still activates the real radio, switching the variant
              // while our popup cleanup (line below) never runs — so the old
              // gold border sticks around on the previously selected pill.
              if (pill.classList.contains('variant-sold-out')) return;
              const value = pill.getAttribute('data-option-value');
              const realPill = activePopupGroup.querySelector('.product-variant-picker__pill-list [data-option-value="' + _esc(value) + '"]');
              if (realPill) realPill.click();
              updateTrigger(activePopupGroup, value, pill.querySelector('.pill__swatch'));
              popupBody.querySelectorAll('.pill').forEach(p => p.classList.remove('selected'));
              pill.classList.add('selected');
              setTimeout(closePopup, 200);
            });
          }

          if (popupClose) popupClose.addEventListener('click', closePopup);
          if (popupScrim) popupScrim.addEventListener('click', closePopup);

          // Drag to close on mobile
          if (popupDrag && popup) {
            let dragStartY = 0, dragCurrentY = 0;
            popupDrag.addEventListener('touchstart', (e) => {
              dragStartY = e.touches[0].clientY;
              popup.style.transition = 'none';
            }, { passive: true });
            popupDrag.addEventListener('touchmove', (e) => {
              dragCurrentY = e.touches[0].clientY;
              popup.style.transform = 'translateY(' + Math.max(0, dragCurrentY - dragStartY) + 'px)';
            }, { passive: true });
            popupDrag.addEventListener('touchend', () => {
              popup.style.transition = '';
              popup.style.transform = '';
              if (dragCurrentY - dragStartY > 80) closePopup();
              dragStartY = 0;
              dragCurrentY = 0;
            });
          }

          // Trigger clicks & pill list clicks per dropdown
          picker.querySelectorAll('[data-smaak-dropdown]').forEach(dropdown => {
            const trigger = dropdown.querySelector('[data-smaak-dropdown-trigger]');
            const optionGroup = dropdown.closest('.variant-option-group');
            if (!trigger || !optionGroup) return;

            trigger.addEventListener('click', (e) => {
              e.stopPropagation();
              if (dropdown.hasAttribute('data-disabled')) return;
              if (isMobile() && popup) {
                openPopup(optionGroup);
                return;
              }
              const isOpen = optionGroup.hasAttribute('data-dropdown-open');
              picker.querySelectorAll('.variant-option-group[data-dropdown-open]').forEach(g => {
                if (g !== optionGroup) {
                  g.removeAttribute('data-dropdown-open');
                  const pl = g.querySelector('.product-variant-picker__pill-list');
                  if (pl) pl.style.cssText = '';
                  g.style.position = '';
                }
              });
              optionGroup.toggleAttribute('data-dropdown-open', !isOpen);

              const pillList = optionGroup.querySelector('.product-variant-picker__pill-list');
              if (!isOpen && pillList) {
                const triggerEl = optionGroup.querySelector('.smaak-mobile-dropdown');
                const topOffset = triggerEl ? (triggerEl.offsetTop + triggerEl.offsetHeight) : optionGroup.offsetHeight;
                pillList.style.cssText = 'display:flex!important;position:absolute!important;top:' + topOffset + 'px!important;bottom:auto!important;left:0!important;right:0!important;margin-top:2px;flex-wrap:wrap;gap:8px;padding:12px;background:#fff;border:2px solid rgba(0,0,0,0.1);border-radius:1.2rem;box-shadow:0 4px 12px rgba(0,0,0,0.08);z-index:10;';
                optionGroup.style.position = 'relative';
                const floatEl = optionGroup.querySelector('float-element');
                if (floatEl) floatEl.style.display = 'none';
              } else if (isOpen && pillList) {
                pillList.style.cssText = '';
                optionGroup.style.position = '';
              }
            });

            const pillList = optionGroup.querySelector('.product-variant-picker__pill-list');
            if (pillList) {
              pillList.addEventListener('click', (e) => {
                const pill = e.target.closest('.pill');
                if (!pill) return;
                if (pill.classList.contains('variant-unavailable')) return;
                updateTrigger(optionGroup, pill.getAttribute('data-option-value'), pill.querySelector('.pill__swatch'));
                optionGroup.removeAttribute('data-dropdown-open');
                pillList.style.cssText = '';
                optionGroup.style.position = '';
                if (optionGroup.closest('.quick-view__content')) {
                  const trig = optionGroup.querySelector('.smaak-mobile-dropdown');
                  if (trig) setTimeout(() => trig.scrollIntoView({ behavior: 'smooth', block: 'nearest' }), 50);
                }
              });
            }
          });

          // Outside-click sluiting loopt via de ene document-listener
          // onderaan dit bestand — zie registerOutsideClickCloser().
  }

  /**
   * Sluit open desktop-dropdowns bij een klik erbuiten.
   *
   * Stond hier eerst per picker: elke `initVariantPicker` hing z'n eigen
   * `document.addEventListener('click', ...)` op met `picker` in de closure.
   * Op een collectiepagina init'te elke quick-view-open een verse (gekloonde)
   * picker, dus per open kwam er een document-listener bij die een inmiddels
   * losgekoppelde picker in leven hield. Gemeten over vijf keer openen en
   * sluiten, met GC geforceerd en de quick-view-cache geleegd: ~9.000 nodes en
   * ~190 listeners die niet meer vrijkwamen. En elke klik waar dan ook op de
   * pagina liep vervolgens door álle oude listeners heen, dus klikken werd
   * steeds trager naarmate je meer quick-views had bekeken.
   *
   * Nu één listener voor de hele pagina die live in de DOM zoekt in plaats van
   * een picker vast te houden. Zelfde gedrag, geen groei.
   *
   * Let op de opruiming hieronder: de oude per-picker listener haalde alleen
   * `data-dropdown-open` weg en liet de inline cssText op de pill-list staan.
   * Dat viel niet op omdat de globale handler in smaak-dropdown.js — die láter
   * geregistreerd wordt en dus later draait — de cssText alsnog wiste. Deze
   * listener wordt eerder geregistreerd (variant-picker-dropdown.js staat vóór
   * smaak-dropdown.js in theme.liquid), dus zou die opruiming z'n groep niet
   * meer vinden: de attribuut is dan al weg. Daarom ruimt hij hier zelf op,
   * los van de volgorde waarin de listeners toevallig staan.
   */
  function registerOutsideClickCloser() {
    if (window.__cnVariantDropdownOutsideClick) return;
    window.__cnVariantDropdownOutsideClick = true;

    document.addEventListener('click', (e) => {
      if (window.innerWidth <= 768) return;
      if (e.target.closest && e.target.closest('#smaakOverlaySheet')) return;
      document
        .querySelectorAll('variant-picker .variant-option-group[data-dropdown-open]')
        .forEach((g) => {
          if (g.contains(e.target)) return;
          g.removeAttribute('data-dropdown-open');
          const pillList = g.querySelector('.product-variant-picker__pill-list');
          if (pillList) pillList.style.cssText = '';
          g.style.position = '';
        });
    });
  }

  window.cnInitVariantPicker = initVariantPicker;

  registerOutsideClickCloser();

  // Drain any pickers that were queued before this script finished loading
  if (Array.isArray(window.__cnVariantPickerQueue)) {
    window.__cnVariantPickerQueue.forEach(initVariantPicker);
  }

  // Initial sweep — covers PDP / non-AJAX renders where pickers are already
  // in the DOM before this script runs.
  function sweep() {
    document
      .querySelectorAll('variant-picker:not([data-dropdown-init])')
      .forEach(initVariantPicker);
  }
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', sweep);
  } else {
    sweep();
  }
})();
